// Rate limiter utility for ROI calculations
const RATE_LIMIT_KEY = 'roi_calculator_usage';
const MAX_CALCULATIONS = 3;
const TIME_WINDOW_HOURS = 24;

// Disable rate limiting for localhost development
const isLocalhost = window.location.hostname === 'localhost' || 
                   window.location.hostname === '127.0.0.1' ||
                   window.location.hostname.includes('localhost');

/**
 * Get current rate limit status
 * @returns {Object} { canCalculate: boolean, remaining: number, resetTime: Date|null, timeUntilReset: string|null }
 */
export const getRateLimitStatus = () => {
  // Always allow calculations on localhost
  if (isLocalhost) {
    return {
      canCalculate: true,
      remaining: 999,
      resetTime: null,
      timeUntilReset: null
    };
  }

  try {
    const stored = localStorage.getItem(RATE_LIMIT_KEY);
    const now = new Date();
    
    if (!stored) {
      return {
        canCalculate: true,
        remaining: MAX_CALCULATIONS,
        resetTime: null,
        timeUntilReset: null
      };
    }

    const { calculations, firstCalculationTime } = JSON.parse(stored);
    const firstCalcDate = new Date(firstCalculationTime);
    const timeWindowMs = TIME_WINDOW_HOURS * 60 * 60 * 1000;
    const resetTime = new Date(firstCalcDate.getTime() + timeWindowMs);

    // Check if time window has expired
    if (now >= resetTime) {
      // Reset the counter
      localStorage.removeItem(RATE_LIMIT_KEY);
      return {
        canCalculate: true,
        remaining: MAX_CALCULATIONS,
        resetTime: null,
        timeUntilReset: null
      };
    }

    const remaining = Math.max(0, MAX_CALCULATIONS - calculations.length);
    const canCalculate = remaining > 0;
    
    // Calculate time until reset
    const msUntilReset = resetTime.getTime() - now.getTime();
    const hoursUntilReset = Math.floor(msUntilReset / (1000 * 60 * 60));
    const minutesUntilReset = Math.floor((msUntilReset % (1000 * 60 * 60)) / (1000 * 60));
    
    let timeUntilReset;
    if (hoursUntilReset > 0) {
      timeUntilReset = `${hoursUntilReset}h ${minutesUntilReset}m`;
    } else {
      timeUntilReset = `${minutesUntilReset}m`;
    }

    return {
      canCalculate,
      remaining,
      resetTime,
      timeUntilReset
    };
  } catch (error) {
    console.error('Error checking rate limit:', error);
    // On error, allow calculation but log the issue
    return {
      canCalculate: true,
      remaining: MAX_CALCULATIONS,
      resetTime: null,
      timeUntilReset: null
    };
  }
};

/**
 * Record a calculation attempt
 * @returns {Object} Updated rate limit status
 */
export const recordCalculation = () => {
  // Skip recording on localhost
  if (isLocalhost) {
    return getRateLimitStatus();
  }

  try {
    const now = new Date();
    const stored = localStorage.getItem(RATE_LIMIT_KEY);

    if (!stored) {
      // First calculation
      const newData = {
        calculations: [now.toISOString()],
        firstCalculationTime: now.toISOString()
      };
      localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(newData));
    } else {
      const data = JSON.parse(stored);
      const firstCalcDate = new Date(data.firstCalculationTime);
      const timeWindowMs = TIME_WINDOW_HOURS * 60 * 60 * 1000;
      const resetTime = new Date(firstCalcDate.getTime() + timeWindowMs);

      // Check if time window has expired
      if (now >= resetTime) {
        // Reset and start new window
        const newData = {
          calculations: [now.toISOString()],
          firstCalculationTime: now.toISOString()
        };
        localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(newData));
      } else {
        // Add to existing window
        data.calculations.push(now.toISOString());
        localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(data));
      }
    }

    return getRateLimitStatus();
  } catch (error) {
    console.error('Error recording calculation:', error);
    return getRateLimitStatus();
  }
};

/**
 * Clear rate limit data (for testing purposes)
 */
export const clearRateLimit = () => {
  try {
    localStorage.removeItem(RATE_LIMIT_KEY);
  } catch (error) {
    console.error('Error clearing rate limit:', error);
  }
}; 